import {
	Tabs,
	Typography,
	Card,
	Button,
	Switch,
	Tooltip,
	message,
	Space,
	Tag,
	Modal,
} from 'antd'
import { LeftCircleOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getSingleClientAction } from 'slices/singleClient'
import toggleClientStatus from 'api/clients/toggleClientStatus'
import OverViewTable from 'component/Client/OverViewTable'
import WalletTable from 'component/Client/WalletTable'
import UpdateVendorDetailsForm from 'component/Client/UpdateVendorDetailsForm'
import InvoiceTable from 'component/Client/InvoiceTable'
import PaymentsTable from 'component/Client/PaymentsTable'
import UsersLayout from 'container/UsersLayout'
import UpdateWhatsappDetailsForm from 'component/Client/UpdateWhatsAppDetailsForm'
import EditClientForm from 'container/EditClientForm'
import toggleClientSuspension from 'api/clients/toggleClientSuspension'
import archiveClients from 'api/clients/archiveClients'
import TemplatesPageLayout from 'container/TemplatesPageLayout'
import { clearInvoice } from 'slices/invoice'
import { clearPayment } from 'slices/payment'
import { clearTemplates } from 'slices/template'
import { clearUsers } from 'slices/users'
import ClientBroadcasts from 'component/Broadcasts/ClientBroadcasts'
import ReactJson from 'react-json-view'
import DashboardView from 'component/Client/DashboardView'

// import UpgradePlanButton from 'component/Client/UpgradePlanButton'
import WebhookJSON from 'component/Client/WebhookJSON'
import ClientWabaManagement from 'component/Client/WabaManagement'
import TrialManagement from 'component/Client/TrialManagement'
import SignalsCallLogsTable from 'component/Client/SignalsCallLogsTable'
import AdminAccess from 'component/Client/AdminAccess'
import DemoData from 'component/Client/DemoData'

const { TabPane } = Tabs

const { Title } = Typography

const SingleClientPageLayout = () => {
	const [togglingSuspension, setTogglingSuspension] = useState(false)
	const [restoring, setRestoring] = useState(false)

	const navigate = useNavigate()
	const [clientId, setClientId] = useState('')
	const clientParam = useParams()
	const dispatch = useAppDispatch()
	const { clientProfile, planHistory, loading } = useAppSelector(
		state => state.singleClient
	)

	useEffect(() => {
		const { id } = clientParam
		// eslint-disable-next-line eqeqeq
		if (id !== undefined) {
			dispatch(getSingleClientAction(id))
			setClientId(id)
		}
	}, [clientParam])

	/**
	 * Back to Templates Button
	 */
	const handleBack = () => {
		navigate('/clients')
	}

	/**
	 * Toggle client active status function
	 */
	const handleToggleActiveStatus = async (id: string) => {
		try {
			await toggleClientStatus(id)
			await dispatch(getSingleClientAction(id))
			message.success(
				`Client ${clientProfile?.active ? 'deactivated' : 'activated'}`
			)
			// eslint-disable-next-line no-empty
		} catch (error) {}
	}

	// Switch toggle
	const onChangeActive = () => {
		const { id } = clientParam
		if (id !== undefined) {
			handleToggleActiveStatus(id)
		}
	}

	const onUpdateVendorDetails = (val: boolean) => {
		const { id } = clientParam
		if (val && id !== undefined) {
			dispatch(getSingleClientAction(id))
		}
	}

	const handleRestore = () => {
		if (!clientProfile) return
		Modal.confirm({
			title: 'Restore this client?',
			content:
				'They will return to the main clients list. Suspension caused by archiving is lifted. Accounts that were already suspended stay suspended.',
			okText: 'Restore',
			onOk: async () => {
				try {
					setRestoring(true)
					await archiveClients([clientProfile._id], false)
					message.success('Client restored')
					dispatch(getSingleClientAction(clientProfile._id))
				} finally {
					setRestoring(false)
				}
			},
		})
	}

	const handleSuspensionUpdate = async () => {
		try {
			setTogglingSuspension(true)
			if (clientProfile) {
				await toggleClientSuspension(clientProfile._id)
				dispatch(getSingleClientAction(clientProfile._id))
			}
		} finally {
			setTogglingSuspension(false)
		}
	}
	// const getAPP=
	useEffect(() => {
		return () => {
			dispatch(clearInvoice())
			dispatch(clearPayment())
			dispatch(clearTemplates())
			dispatch(clearUsers())
		}
	}, [clientProfile, dispatch])

	return (
		<Card
			title={
				<Space>
					<Title level={3} style={{ marginBottom: 0 }}>
						{clientProfile?.organizationName}
					</Title>
					{clientProfile?.archived && <Tag>Archived</Tag>}
				</Space>
			}
			extra={
				<Space size='large'>
					{/* {(clientProfile?.isFreePlan ||
							clientProfile?.platformMode === 'DEMO') && <UpgradePlanButton />} */}

					<Tooltip
						placement='bottomRight'
						title={`Switch to ${
							clientProfile?.active ? 'Inactive' : 'Active'
						} client`}
					>
						<Switch
							loading={loading}
							checked={!!clientProfile?.active}
							onChange={onChangeActive}
						/>
					</Tooltip>
					{clientProfile?.archived && (
						<Button onClick={handleRestore} loading={restoring || loading}>
							Restore
						</Button>
					)}
					<Tooltip
						placement='bottomRight'
						title={
							clientProfile?.archived && clientProfile?.accountSuspended
								? 'Restore this client before removing suspension'
								: `${
										clientProfile?.accountSuspended ? 'Deactivate' : 'Activate'
								  } Payment Only Mode`
						}
					>
						<Button
							danger={clientProfile?.accountSuspended === false}
							onClick={handleSuspensionUpdate}
							loading={togglingSuspension || loading}
							disabled={!!clientProfile?.archived && !!clientProfile?.accountSuspended}
						>
							{clientProfile?.accountSuspended ? 'Un-Suspend' : 'Suspend'}
						</Button>
					</Tooltip>
					<Tooltip placement='bottomRight' title='Back'>
						<Button
							type='primary'
							icon={<LeftCircleOutlined />}
							style={{ marginLeft: '10px' }}
							onClick={handleBack}
						>
							Back
						</Button>
					</Tooltip>
				</Space>
			}
		>
			<Tabs type='card' destroyInactiveTabPane>
				{!!clientProfile && (
					<>
						<TabPane tab='Edit Client Details' key='edit-details'>
							<EditClientForm
								closeDrawer={async () => {
									if (clientId) {
										await dispatch(getSingleClientAction(clientId))
									}
								}}
								singleClient={clientProfile}
							/>
						</TabPane>
						<TabPane tab='Trial' key='trial'>
							<TrialManagement
								clientProfile={clientProfile ?? null}
								onSuccess={() => clientId && dispatch(getSingleClientAction(clientId))}
							/>
						</TabPane>
						<TabPane tab='Demo Data' key='demo-data'>
							{(clientId || clientProfile?._id) && (
								<DemoData clientId={clientId || clientProfile._id} />
							)}
						</TabPane>
						<TabPane tab='Dashboard' key='11'>
							{clientId && <DashboardView clientId={clientId} />}
						</TabPane>
						<TabPane tab='Overview' key='1'>
							<OverViewTable
								overView={clientProfile}
								planHis={planHistory}
								loading={loading}
							/>
						</TabPane>
						<TabPane tab='Wallet' key='2'>
							<WalletTable clientProfile={clientProfile} />
						</TabPane>
						<TabPane tab='Update Vendor Details' key='3'>
							<UpdateVendorDetailsForm
								clientData={clientProfile}
								onSuccess={onUpdateVendorDetails}
							/>
						</TabPane>
						{clientProfile?.vendorSelected &&
							[
								'THREE_SIXTY_DIALOG',
								'THREE_SIXTY_DIALOG_CLOUD',
								'WHATSAPP_CLOUD',
								'PINNACLE',
								'PINNACLE_V3',
							].includes(clientProfile?.vendorSelected) && (
								<TabPane tab='Update WhatsApp Details' key='4'>
									<UpdateWhatsappDetailsForm
										clientId={clientId}
										vendorType={clientProfile?.vendorSelected}
									/>
								</TabPane>
							)}
						{clientProfile?.vendorSelected &&
							['WHATSAPP_CLOUD', 'PINNACLE', 'PINNACLE_V3'].includes(
								clientProfile?.vendorSelected
							) && (
								<TabPane tab='Waba Management' key='5'>
									<ClientWabaManagement clientId={clientId || clientProfile?._id} />
								</TabPane>
							)}
						{(clientProfile?.vendorSelected === 'PINNACLE' ||
							clientProfile?.vendorSelected === 'PINNACLE_V3') && (
							<TabPane tab='Webhook JSON' key='Webhookjson'>
								<WebhookJSON />
							</TabPane>
						)}
						<TabPane tab='Users' key='6'>
							<UsersLayout clientId={clientId} />
						</TabPane>
						<TabPane tab='Templates' key='7'>
							<TemplatesPageLayout client={clientId} />
						</TabPane>
						<TabPane tab='Invoice' key='8'>
							<InvoiceTable clientId={clientId} />
						</TabPane>
						<TabPane tab='Payments' key='9'>
							<PaymentsTable clientId={clientId} />
						</TabPane>
						<TabPane tab='Broadcasts' key='10'>
							<ClientBroadcasts clientId={clientProfile?._id || ''} />
						</TabPane>
						<TabPane tab='Signals Call Logs' key='signals'>
							<SignalsCallLogsTable clientId={clientId || clientProfile?._id || ''} />
						</TabPane>
						<TabPane tab='Admin Access' key='admin-access'>
							<AdminAccess clientId={clientId || clientProfile?._id || ''} />
						</TabPane>
						<TabPane tab='JSON' key='json'>
							<ReactJson
								src={clientProfile || {}}
								theme='google'
								indentWidth={8}
								style={{
									padding: 16,
									borderRadius: 8,
								}}
							/>
						</TabPane>
					</>
				)}
			</Tabs>
		</Card>
	)
}

export default SingleClientPageLayout
